/**
 * "Download (N files)" — one implementation for the Files browser and the
 * Shared view (#1692).
 *
 * ### N presigned downloads, not one zip
 *
 * Decided on prod's numbers (2026-10-22, read-only): a flight CSV is 2.4 MB at
 * the median, 12 MB at p90, 38 MB at p99 and 147 MB at the largest; a folder
 * holds 3 files at the median and 178 at most, and the Shared view pages 50 at a
 * time by default. A zip endpoint would carry a 600 MB selection through the
 * backend on one long request, against Railway's request limits, and it would
 * need its own authorization for N ids. Each file already has an endpoint that
 * authorizes it (`GET /api/file-objects/{id}/download` asks the database whether
 * the caller may SELECT the row) and answers with a presigned URL, so the bytes
 * go from S3 to the browser and never through the backend. What it costs is one
 * browser prompt, the first time a site starts several downloads ("Download
 * multiple files? Allow"), which the progress line warns about.
 *
 * ### Why a hidden iframe, not `window.open` or a link click
 *
 * Measured in Chromium with a cross-site URL that answers
 * `Content-Disposition: attachment`, as a presigned URL from that endpoint does:
 *
 * - `window.open` is a popup. A browser lets one through per click, so the
 *   second file of a selection would be blocked. (One file still opens this way;
 *   see `handleDownload` in each view.)
 * - A link click downloads every file, but a URL that answers an error
 *   (403 from S3 on an expired or missing object) NAVIGATES THE WHOLE APP to the
 *   S3 XML error page.
 * - A hidden iframe downloads every file without a user gesture, and an error
 *   stays inside the invisible frame.
 *
 * The run is sequential, one presign at a time with a short pause between
 * deliveries, so a 50-file selection is 50 small API calls in a row rather than
 * 50 at once, and the count it shows is the count actually started.
 */
import type { FileItem } from './types';
import { entriesForAction } from './selectionModel';
import { isDownloadableEntry } from './childrenModel';

export interface BulkDownloadStatus {
  /** True until the last file has been handed to the browser or has failed. */
  running: boolean;
  /** Files dealt with so far, started or failed. */
  done: number;
  total: number;
  /** Files handed to the browser. */
  started: number;
  /** The names of the files whose URL could not be had, in selection order. */
  failedNames: string[];
}

export interface BulkDownloadDeps<T> {
  nameOf: (target: T) => string;
  /** The presigned URL for one target, or undefined when the server gave none. */
  presign: (target: T) => Promise<string | undefined>;
  /** Hand one URL to the browser. `deliverInHiddenFrame` in the app. */
  deliver: (url: string) => void;
  onProgress: (status: BulkDownloadStatus) => void;
  /** The gap between two deliveries. A browser can drop downloads started in one tick. */
  pause: () => Promise<void>;
}

/** The pause the app uses between two deliveries; tests pass their own. */
export const BULK_DOWNLOAD_GAP_MS = 300;

/**
 * Download every target, one after another, reporting progress after each.
 *
 * A failure is counted and the run goes on: one file whose share was revoked
 * between the listing and the click must not cost the user the other 49. The
 * returned status is the final one, and it says which files did not start.
 */
export async function runBulkDownload<T>(
  targets: readonly T[],
  deps: BulkDownloadDeps<T>
): Promise<BulkDownloadStatus> {
  let status: BulkDownloadStatus = {
    running: targets.length > 0,
    done: 0,
    total: targets.length,
    started: 0,
    failedNames: [],
  };
  deps.onProgress(status);
  for (const [index, target] of targets.entries()) {
    let url: string | undefined;
    try {
      url = await deps.presign(target);
    } catch {
      url = undefined;
    }
    let started = false;
    if (url) {
      if (index > 0) await deps.pause();
      // A delivery that throws (a URL `deliverInHiddenFrame` refuses) is that
      // file's failure, not the run's: the run must still finish and say so.
      try {
        deps.deliver(url);
        started = true;
      } catch {
        started = false;
      }
    }
    status = {
      ...status,
      done: status.done + 1,
      started: status.started + (started ? 1 : 0),
      failedNames: started ? status.failedNames : [...status.failedNames, deps.nameOf(target)],
    };
    status = { ...status, running: status.done < status.total };
    deps.onProgress(status);
  }
  return status;
}

/** How long a delivery frame stays in the page: the download has been handed off long before. */
const FRAME_LIFETIME_MS = 120_000;

/**
 * Start one download in an invisible iframe (see the module comment for why).
 *
 * The frame is removed after two minutes. By then the browser has read the
 * response headers and handed the transfer to its download manager, so removing
 * the frame does not cancel it.
 *
 * Only an `https:` URL is loaded. The URL is always a presigned S3 URL from our
 * own API, so this never refuses one in practice; it is there so that a
 * `javascript:` or `data:` URL, from whatever bug, can never run in the app's
 * origin through a frame this module made.
 */
export function deliverInHiddenFrame(url: string, doc: Document = document): void {
  if (new URL(url, doc.baseURI).protocol !== 'https:') {
    throw new Error('refusing to deliver a download URL that is not https');
  }
  const frame = doc.createElement('iframe');
  frame.style.display = 'none';
  frame.setAttribute('aria-hidden', 'true');
  frame.dataset.bulkDownload = 'true';
  frame.src = url;
  doc.body.appendChild(frame);
  setTimeout(() => frame.remove(), FRAME_LIFETIME_MS);
}

/**
 * The i18n key for the download item, given how many files it will fetch.
 *
 * The menu describes its TARGETS, the rule `shareMenuLabelKey` follows: with
 * five files selected, "Download" on the clicked one read as one file and
 * fetched one file, and the other four were silently left behind.
 */
export type DownloadMenuLabelKey = 'fileManagement.download' | 'fileManagement.downloadMany';

export function downloadMenuLabelKey(targetCount: number): DownloadMenuLabelKey {
  return targetCount > 1 ? 'fileManagement.downloadMany' : 'fileManagement.download';
}

/**
 * Which entries a Download on `clicked` fetches in the Files browser: the
 * selection when the clicked entry is part of it (`entriesForAction`, the rule
 * share and drag already follow), keeping only what has bytes to fetch.
 *
 * A folder is left out, not walked: downloading a folder would be a different
 * feature (its whole subtree, at any depth), and a selection that holds one
 * still downloads the files beside it.
 */
export function downloadTargets(
  clicked: FileItem,
  selectedIds: ReadonlySet<string>,
  visible: readonly FileItem[]
): FileItem[] {
  return entriesForAction(clicked, selectedIds, visible).filter(isDownloadableEntry);
}
